import React from 'react';
import PropTypes from 'prop-types';

import {Box, IconButton, Typography} from 'views/components';
import {MoreHoriz} from 'views/icons';
import {Translate, translate} from 'views/module';

const ScheduleEventDisplayFlexible = (props) => {
  const {preferredDays, preferredTimes, visitDuration, onMenuClick} = props;

  const daysOfWeek = React.useMemo(() => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], []);
  const dayLabelById = React.useMemo(() => ({
    ANY_DAY: translate('schedulestep.ScheduleEventEditFlexible.DayOptions.AnyDay'),
    Sun: translate('schedulestep.ScheduleEventEditFlexible.DayOptions.Sun'),
    Mon: translate('schedulestep.ScheduleEventEditFlexible.DayOptions.Mon'),
    Tue: translate('schedulestep.ScheduleEventEditFlexible.DayOptions.Tue'),
    Wed: translate('schedulestep.ScheduleEventEditFlexible.DayOptions.Wed'),
    Thu: translate('schedulestep.ScheduleEventEditFlexible.DayOptions.Thu'),
    Fri: translate('schedulestep.ScheduleEventEditFlexible.DayOptions.Fri'),
    Sat: translate('schedulestep.ScheduleEventEditFlexible.DayOptions.Sat'),
  }), []);
  const timeLabelById = React.useMemo(() => ({
    ANY_TIME: translate('schedulestep.ScheduleEventEditFlexible.TimeOptions.AnyTime'),
    MORNING: translate('schedulestep.ScheduleEventEditFlexible.TimeOptions.Morning'),
    AFTERNOON: translate('schedulestep.ScheduleEventEditFlexible.TimeOptions.Afternoon'),
    EVENING: translate('schedulestep.ScheduleEventEditFlexible.TimeOptions.Evening'),
    OVERNIGHT: translate('schedulestep.ScheduleEventEditFlexible.TimeOptions.Overnight'),
  }), []);

  const formatLabelList = React.useCallback((labels) => {
    if (!labels.length) {
      return '';
    }
    if (labels.length === 1) {
      return labels[0];
    }
    if (labels.length === 2) {
      return `${labels[0]} & ${labels[1]}`;
    }
    const first = labels.slice(0, -1).join(', ');
    const last = labels[labels.length - 1];
    return `${first} & ${last}`;
  }, []);

  const sortDays = React.useCallback((dayIds) => {
    return [...dayIds].sort((dayA, dayB) => daysOfWeek.indexOf(dayA) - daysOfWeek.indexOf(dayB));
  }, [daysOfWeek]);

  const displayTitle = React.useMemo(() => {
    if (!preferredDays.length) {
      return translate('schedulestep.ScheduleCard.CustomSchedule');
    }
    if (preferredDays.includes('ANY_DAY')) {
      return dayLabelById.ANY_DAY;
    }
    const sortedDays = sortDays(preferredDays);
    const labels = sortedDays.map((dayId) => dayLabelById[dayId]);
    return formatLabelList(labels);
  }, [dayLabelById, formatLabelList, preferredDays, sortDays]);

  const displayTimeOfDay = React.useMemo(() => {
    if (!preferredTimes.length) {
      return '';
    }
    if (preferredTimes.includes('ANY_TIME')) {
      return timeLabelById.ANY_TIME;
    }
    const labels = preferredTimes.map((timeId) => timeLabelById[timeId]);
    return formatLabelList(labels);
  }, [formatLabelList, preferredTimes, timeLabelById]);

  return (
    <Box
      sx={{
        border: '1px dashed #C5BBB9',
        borderRadius: '24px',
        p: '32px',
        backgroundColor: 'white',
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2}}>
        <Typography sx={{fontSize: '20px', fontWeight: 300, color: '#2D2925', lineHeight: '128%', fontFamily: '"Spectral", serif'}}>
          {displayTitle}
          <Box component='span' sx={{color: '#2D2925A3'}}>
            <Translate expression={'schedulestep.ScheduleEventDisplayFlexible.FlexibleSuffix'} />
          </Box>
        </Typography>
        <IconButton
          onClick={onMenuClick}
          sx={{border: '1px solid #C5BBB9', borderRadius: '8px', p: '4px'}}
        >
          <MoreHoriz sx={{fontSize: 24}} />
        </IconButton>
      </Box>

      <Box sx={{display: 'flex', gap: '72px', flexWrap: 'wrap'}}>
        <Box>
          <Typography sx={{fontSize: '16px', fontWeight: 590, color: '#2D2925', lineHeight: '150%'}}>
            <Translate expression={'schedulestep.ScheduleEventDisplayFlexible.DurationLabel'} />
          </Typography>
          <Typography sx={{fontSize: '16px', fontWeight: 500, color: '#2D2925A3', lineHeight: '150%'}}>
            {visitDuration}
          </Typography>
        </Box>

        <Box>
          <Typography sx={{fontSize: '16px', fontWeight: 590, color: '#2D2925', lineHeight: '150%'}}>
            <Translate expression={'schedulestep.ScheduleEventDisplayFlexible.TimeLabel'} />
          </Typography>
          <Typography sx={{fontSize: '16px', fontWeight: 500, color: '#2D2925A3', lineHeight: '150%'}}>
            {displayTimeOfDay}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

ScheduleEventDisplayFlexible.propTypes = {
  preferredDays: PropTypes.arrayOf(PropTypes.string),
  preferredTimes: PropTypes.arrayOf(PropTypes.string),
  visitDuration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onMenuClick: PropTypes.func,
};

export default React.memo(ScheduleEventDisplayFlexible);
